import React, { useMemo, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ReactComponent as FilterIcon } from '../../../assets/PepWise2.0/Filter - Icon - New.svg';
import type { FilterConfigSection, FilterValues } from './type';
import MultiFilterPanelPopup from './MultiFilterPanelPopup';

type Props = {
  sections: FilterConfigSection[];

  appliedValues: FilterValues;
  setAppliedValues: React.Dispatch<React.SetStateAction<FilterValues>>;
  // Apply callback
  onApplyFilters: (values: FilterValues, meta: { isAnyFilterApplied: boolean }) => void;
  onClear?: () => void;
  // Optional UI props
  popupWidth?: number;
  maxHeight?: number;
  disabled?: boolean;

  clearText?: string;
  clearColor?: string;

  icon?: React.ReactNode;
};

const getActiveFilterCount = (values: FilterValues) => {
  if (!values) return 0;

  let count = 0;
  Object.values(values).forEach((v) => {
    if (v == null) return;
    if (Array.isArray(v)) count += v.length;
    else if (String(v).trim() && v !== 'all') count += 1;
  });

  return count;
};

const clearValuesLike = (values: FilterValues): FilterValues => {
  const cleared: FilterValues = {};
  Object.keys(values || {}).forEach((k) => {
    const v = values[k];
    cleared[k] = Array.isArray(v) ? [] : null;
  });
  return cleared;
};

export default function MultiFilterPanel({
  sections,
  appliedValues,
  setAppliedValues,
  onApplyFilters,
  onClear,
  popupWidth = 340,
  maxHeight = 520,
  disabled = false,
  clearText = 'CLEAR FILTER',
  clearColor,
  icon,
}: Props) {
  const theme = useTheme();
  const colors = theme.palette;

  const resolvedClearColor = clearColor ?? colors.filterPanel.triggerClearText;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const activeCount = useMemo(() => getActiveFilterCount(appliedValues), [appliedValues]);

  const hasActive = activeCount > 0;

  const handleClear = () => {
    const cleared = clearValuesLike(appliedValues);
    setAppliedValues(cleared);
    if (onClear) {
      onClear();
    } else {
      onApplyFilters(cleared, { isAnyFilterApplied: false });
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          disabled={disabled || !sections?.length}
          sx={{ position: 'relative' }}
        >
          {icon ?? <FilterIcon />}

          {hasActive && (
            <Box
              component="span"
              sx={{
                position: 'absolute',
                top: -1,
                right: -10,
                minWidth: 16,
                height: 16,
                px: 0.1,
                borderRadius: 50,
                bgcolor: resolvedClearColor,
                color: colors.filterPanel.triggerBadgeText,
                fontSize: 12,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
              }}
            >
              {activeCount}
            </Box>
          )}
        </IconButton>
      </Box>

      {hasActive && (
        <Typography
          onClick={handleClear}
          sx={{
            cursor: 'pointer',
            color: resolvedClearColor,
            fontWeight: 500,
            fontSize: 14,
            textTransform: 'uppercase',
            userSelect: 'none',
          }}
        >
          {clearText}
        </Typography>
      )}

      <MultiFilterPanelPopup
        sections={sections}
        defaultValues={appliedValues}
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        onApply={(values, meta) => {
          setAppliedValues(values);
          onApplyFilters(values, meta);
        }}
        popupWidth={popupWidth}
        maxHeight={maxHeight}
      />
    </Box>
  );
}
